import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import React, { FC, useState } from 'react';
import { palette, SimpleLoader } from '@dev/ee-components';
import { isEmpty } from 'lodash-es';
import { Grid, Link, Portal } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Recipient, RecipientEvent } from '@dev/ee-api/ee-api-models';
import { useDispatch, useSelector } from 'react-redux';

import SingleContact from '@api/components/Contacts/SingleContact';
import ViewEmail from '@api/components/Statistics/ViewEmail';
import { useMobile } from '@api/services/hooks/useMobile';
import { OverlayComponent } from '@api/components/Overlay';
import { isReseller } from '@api/services/Security';
import { ShowDialog } from '@api/store/dialog/actions';
import { ResendStart } from '@api/store/logs/actions';
import { DEFAULT_PER_PAGE_COUNT } from '@api/services/constants';
import { LogTypesString } from '@api/store/logs/types';
import { links } from '@api/services/dictionaries/links';

import DefaultContactAvatar from '../../../../images/avatar-contact.svg';
import { useLogsTableData } from '../../hooks/useLogsTableData';
import { isLogsScreen, isResendAllowed } from '../../helperFunctions';
import { LogsTableInterface } from '../../Interface';
import { DesktopLogsTable, GoToTopFab, MobileLogsTable } from './components';
import { getAccountStore } from '@api/store/account/actions';

const SkeletonTemplate = (isSms: boolean) => ({
    to: (
        <Grid container direction="column">
            <Skeleton style={{ margin: '8px 0px 6px 0px' }} />
            <Skeleton width={100} height={10} />
        </Grid>
    ),
    createDate: (
        <Grid container direction="column">
            <Skeleton style={{ margin: '8px 0px 6px 0px' }} />
            <Skeleton width={50} height={10} />
        </Grid>
    ),
    event: <Skeleton />,

    icon: <Skeleton />,
    ...(isSms ? {} : { bouncetype: <Skeleton /> }),
});

const prepareSkeletonRows = (perPage = 10, isSms: boolean) => [...new Array(perPage)].map((_) => SkeletonTemplate(isSms));

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
`;

export type LogTableFilters = 'campaign' | 'channel' | 'event' | 'date' | 'search';

export const LogsTable: FC<LogsTableInterface> = ({
    fetchNextOnScroll,
    selectedEventsCheckboxes,
    onHeadersEventFilterClick,
    eventFiltersType,
}: LogsTableInterface): JSX.Element => {
    const isMobile = useMobile();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { logType } = useParams<{ logType: LogTypesString }>();
    const account = useSelector(getAccountStore);
    const [emailDetails, setEmailDetails] = useState({ open: false, emailId: null });
    const [contactDetails, setContactDetails] = useState(null);

    const onRowIconClick = (msgId: string): void => {
        setEmailDetails({
            open: true,
            emailId: msgId,
        });
    };

    const onRowEmailClick = (singleContactToShowInOverlay) => {
        setContactDetails(singleContactToShowInOverlay);
    };

    const onBounceDetailsClick = (title: string, description: string, recipientEvent: RecipientEvent | Recipient): void => {
        const link = !isReseller(account) && (
            <Link style={{ color: palette.indigo[6] }} href={links.bounceErrorsCategories}>
                {t('learnmore')}
            </Link>
        );
        const logErrorText = (
            <span>
                {description} {link}
            </span>
        );

        dispatch(
            ShowDialog({
                title,
                body: logErrorText,
                cancelLabel: t('cancel'),
                ...(isResendAllowed(recipientEvent)
                    ? {
                          confirmLabel: t('resend'),
                          onClickConfirm: () => {
                              dispatch(
                                  ResendStart({
                                      messageID: recipientEvent.MsgID,
                                      createNew: true,
                                  })
                              );
                          },
                      }
                    : {}),
            })
        );
    };

    const { rows, headers, isLoading, hasMoreLogs } = useLogsTableData(
        { onRowIconClick, onRowEmailClick, onBounceDetailsClick },
        { selectedEventsCheckboxes, onHeadersEventFilterClick, eventFiltersType }
    );

    const tableRows = isLoading ? prepareSkeletonRows(DEFAULT_PER_PAGE_COUNT, isLogsScreen(LogTypesString.SMS, logType)) : rows;

    const infiniteScrollProps = {
        dataLength: tableRows.length || DEFAULT_PER_PAGE_COUNT,
        hasMore: !isEmpty(rows) && hasMoreLogs && tableRows.length % DEFAULT_PER_PAGE_COUNT === 0,
        next: fetchNextOnScroll,
        loader: (
            <LoaderWrapper>
                <SimpleLoader />
            </LoaderWrapper>
        ),
    };

    const getTable = () => {
        if (isMobile) {
            return (
                <MobileLogsTable
                    rows={tableRows}
                    headers={headers}
                    isLoading={isLoading}
                    onViewDetailsClick={onRowIconClick}
                    infiniteScrollProps={infiniteScrollProps}
                />
            );
        }
        return <DesktopLogsTable rows={tableRows} headers={headers} isLoading={isLoading} infiniteScrollProps={infiniteScrollProps} />;
    };

    return (
        <>
            <>
                {getTable()}
                <GoToTopFab />
            </>

            {!!contactDetails && (
                <OverlayComponent open={!!contactDetails} onClose={() => setContactDetails(null)}>
                    <SingleContact
                        isHeaderHidden
                        contactEmail={contactDetails}
                        onClickLogsVisibilityIcon={(email: string, msgId: string) => setEmailDetails({ open: true, emailId: msgId })}
                        contactDefaultIcon={<DefaultContactAvatar />}
                        CloseOverlay={() => setContactDetails(null)}
                    />
                </OverlayComponent>
            )}
            {emailDetails.open && (
                <Portal>
                    <ViewEmail
                        open={emailDetails.open}
                        msgId={emailDetails.emailId}
                        onClose={() => setEmailDetails({ open: false, emailId: null })}
                        isNotificationLog={isLogsScreen(LogTypesString.NOTIFICATION, logType)}
                        isSmsScreen={isLogsScreen(LogTypesString.SMS, logType)}
                    />
                </Portal>
            )}
        </>
    );
};
